import { CheckCircleIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import moment from 'moment'
import { ReactElement, ReactNode } from 'react'
import { LazyLocationCell } from '../LazyLocationCell'
import { formatDate } from 'app/date.utils'
import { Tooltip } from '../Tooltip'
import { getCardField } from 'lib/listCardFields'
import type { CardDensity, CardSlotName } from 'lib/listCardConfig'
import type { TaskCounts } from 'app/hooks/usePipelineTaskCounts'
import type { ContactJson } from 'app/types/entities'

const TASK_STATES = [
  { key: `completed`, label: `Completed tasks`, Icon: CheckCircleIcon, tone: `text-green-700` },
  { key: `open`, label: `Open tasks`, Icon: ClockIcon, tone: `text-blue-700` },
  { key: `overdue`, label: `Overdue tasks`, Icon: ExclamationTriangleIcon, tone: `text-red-700` },
] as const

/** Task counts as icon+count chips — no inline "Tasks" text; the row's label column names it. */
function TaskPills({ counts }: { counts: TaskCounts }): ReactElement {
  return (
    <div className="flex items-center gap-x-2.5">
      {TASK_STATES.map(({ key, label, Icon, tone }) => (
        <Tooltip key={key} message={label} placement="top">
          <span className={classNames(`inline-flex items-center gap-x-0.5`, counts[key] === 0 ? `text-minimal` : tone)}>
            <Icon className="h-3.5 w-3.5" />
            {counts[key]}
          </span>
        </Tooltip>
      ))}
    </div>
  )
}

function agingTone(days: number | null): string {
  if (days == null) return `text-strong`
  if (days > 30) return `text-red-700`
  if (days > 14) return `text-amber-700`
  return `text-strong`
}

/** Days-since-last-contact as tone-colored value text (no chip background). */
function AgingValue({ contact }: { contact: ContactJson }): ReactElement {
  const days = contact.lastCommDate ? moment().diff(moment(contact.lastCommDate), `days`) : null
  return <span className={agingTone(days)}>{days == null ? `No contact yet` : `${days}d since contact`}</span>
}

export function CardSlot({
  slot,
  fieldId,
  contact,
  density,
  taskCounts,
}: {
  slot: CardSlotName
  fieldId: string
  contact: ContactJson
  density: CardDensity
  taskCounts?: TaskCounts
}): ReactElement | null {
  const field = getCardField(fieldId)
  if (!field) return null
  const compact = density === `compact`

  const raw = field.getValue(contact)
  // Date fields carry a date-only value stamped at midnight UTC, so they must be read on the UTC
  // calendar day. A plain local-time parse renders the day before in any behind-UTC zone (an Aug 21
  // appointment showed as Aug 20). `formatDate` is UTC-anchored, matching the drawer and the tables.
  const textValue =
    field.isDate && raw ? formatDate({ value: raw as string, dateOnly: true }) || `-` : (raw as string) || `-`
  const hoverTitle = textValue === `-` ? undefined : textValue

  if (slot === `hero`) {
    return (
      <div className="flex min-w-0 flex-col">
        {/*
         * One type size for every highlight value, long or short, so cards read as a set instead
         * of a ransom note. The fixed line box holds the panel at the same height regardless.
         * text-sm fits ~30 chars against the ~222px panel inside a w-72 column; anything longer
         * truncates with ellipsis and carries the full value in `title`.
         */}
        <div className="flex min-h-[1.875rem] items-center">
          <span title={hoverTitle} className="min-w-0 truncate text-sm font-semibold leading-tight text-strong">
            {textValue}
          </span>
        </div>
        {!compact && <span className="text-body-xs text-subtle">{field.label}</span>}
      </div>
    )
  }

  if (slot === `subtitle`) {
    if (fieldId === `location`) {
      return contact.locationId ? (
        <LazyLocationCell
          locationId={contact.locationId}
          className="truncate text-body-xs text-subtle"
          loadingIndicator
          hoverTitle
        />
      ) : (
        <span className="text-body-xs text-subtle">-</span>
      )
    }
    return (
      <span title={hoverTitle} className="truncate text-body-xs text-subtle">
        {textValue}
      </span>
    )
  }

  // meta — every field renders as one normalized "label   value" row
  let valueNode: ReactNode
  if (fieldId === `tasks`) {
    valueNode = <TaskPills counts={taskCounts ?? { completed: 0, open: 0, overdue: 0 }} />
  } else if (fieldId === `aging`) {
    valueNode = <AgingValue contact={contact} />
  } else if (fieldId === `location`) {
    valueNode = contact.locationId ? (
      <LazyLocationCell locationId={contact.locationId} className="truncate text-inherit" loadingIndicator hoverTitle />
    ) : (
      <span className="text-minimal">-</span>
    )
  } else {
    valueNode = (
      <span title={hoverTitle} className="truncate">
        {textValue}
      </span>
    )
  }

  return (
    <div className="flex min-h-[1.75rem] items-center gap-x-2 text-[11px] leading-tight">
      {!compact && <span className="w-20 shrink-0 truncate text-subtle">{field.label}</span>}
      <div className="flex min-w-0 grow items-center font-medium text-strong">{valueNode}</div>
    </div>
  )
}
